import { useState } from "react";
import { api, formatDate, getErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Trash2, Pencil, RefreshCw, History } from "lucide-react";
import { toast } from "sonner";

const EMPTY_FORM = { name: "", training_date: "", expiry_date: "", organization: "", notes: "" };

export const TrainingsTab = ({ employee, trainings, onRefresh }) => {
  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [renewOpen, setRenewOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [renewForm, setRenewForm] = useState({ training_date: "", expiry_date: "", notes: "" });

  const handleAdd = async () => {
    if (!form.name || !form.training_date) {
      toast.error("Nom et date de formation obligatoires");
      return;
    }
    try {
      await api.addTraining(employee.id, form);
      toast.success("Formation ajoutée");
      setAddOpen(false);
      setForm(EMPTY_FORM);
      onRefresh();
    } catch (e) {
      toast.error(getErrorMessage(e));
    }
  };

  const openEdit = (t) => {
    setSelected(t);
    setForm({
      name: t.name || "",
      training_date: t.training_date || "",
      expiry_date: t.expiry_date || "",
      organization: t.organization || "",
      notes: t.notes || "",
    });
    setEditOpen(true);
  };

  const handleEdit = async () => {
    if (!form.name || !form.training_date) {
      toast.error("Nom et date de formation obligatoires");
      return;
    }
    try {
      await api.updateTraining(selected.id, form);
      toast.success("Formation modifiée");
      setEditOpen(false);
      setSelected(null);
      onRefresh();
    } catch (e) {
      toast.error(getErrorMessage(e));
    }
  };

  const openRenew = (t) => {
    setSelected(t);
    setRenewForm({ training_date: "", expiry_date: "", notes: "" });
    setRenewOpen(true);
  };

  const handleRenew = async () => {
    if (!renewForm.training_date) {
      toast.error("Date du repassage obligatoire");
      return;
    }
    try {
      const payload = {
        training_date: renewForm.training_date,
        expiry_date: renewForm.expiry_date || null,
        notes: renewForm.notes || null,
      };
      await api.renewTraining(selected.id, payload);
      toast.success("Repassage enregistré — historique conservé");
      setRenewOpen(false);
      setSelected(null);
      onRefresh();
    } catch (e) {
      toast.error(getErrorMessage(e));
    }
  };

  const openHistory = (t) => {
    setSelected(t);
    setHistoryOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer cette formation (et tout son historique) ?")) return;
    try {
      await api.deleteTraining(id);
      toast.success("Supprimé");
      onRefresh();
    } catch (e) {
      toast.error(getErrorMessage(e));
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Formations</CardTitle>
          <Button size="sm" onClick={() => { setForm(EMPTY_FORM); setAddOpen(true); }} data-testid="add-training-btn">
            <Plus className="h-4 w-4 mr-1" /> Ajouter
          </Button>
        </CardHeader>
        <CardContent>
          {trainings.length === 0 ? (
            <p className="text-center text-muted-foreground py-4">Aucune formation enregistrée.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Formation</TableHead>
                  <TableHead>Organisme</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Fin de validité</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {trainings.map((t) => {
                  const daysLeft = t.expiry_date ? Math.floor((new Date(t.expiry_date) - new Date()) / 86400000) : null;
                  const isExpired = daysLeft !== null && daysLeft < 0;
                  const isSoon = daysLeft !== null && daysLeft >= 0 && daysLeft <= 270;
                  const historyCount = (t.history || []).length;
                  return (
                    <TableRow
                      key={t.id}
                      className={isExpired ? "bg-red-50" : isSoon ? "bg-amber-50" : ""}
                      data-testid={`training-row-${t.id}`}
                    >
                      <TableCell className="font-medium">{t.name}</TableCell>
                      <TableCell>{t.organization || "-"}</TableCell>
                      <TableCell>{formatDate(t.training_date)}</TableCell>
                      <TableCell>
                        {t.expiry_date
                          ? formatDate(t.expiry_date)
                          : <span className="text-muted-foreground">Pas d&apos;expiration</span>}
                      </TableCell>
                      <TableCell>
                        {isExpired && <Badge variant="destructive">Expirée ({Math.abs(daysLeft)}j)</Badge>}
                        {isSoon && !isExpired && <Badge className="bg-amber-500 text-white">{daysLeft}j restants</Badge>}
                        {daysLeft !== null && daysLeft > 270 && <Badge className="bg-green-100 text-green-800">Valide</Badge>}
                        {daysLeft === null && <Badge variant="outline">-</Badge>}
                      </TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        {historyCount > 0 && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openHistory(t)}
                            title={`Historique (${historyCount})`}
                            data-testid={`training-history-btn-${t.id}`}
                          >
                            <History className="h-4 w-4 text-slate-600" />
                            <span className="ml-1 text-xs">{historyCount}</span>
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openRenew(t)}
                          title="Enregistrer un repassage"
                          data-testid={`training-renew-btn-${t.id}`}
                        >
                          <RefreshCw className="h-4 w-4 text-blue-600" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEdit(t)}
                          title="Modifier"
                          data-testid={`training-edit-btn-${t.id}`}
                        >
                          <Pencil className="h-4 w-4 text-slate-600" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(t.id)}
                          title="Supprimer"
                          data-testid={`training-delete-btn-${t.id}`}
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent data-testid="add-training-dialog">
          <DialogHeader><DialogTitle>Nouvelle formation</DialogTitle></DialogHeader>
          <TrainingFormFields form={form} setForm={setForm} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>Annuler</Button>
            <Button onClick={handleAdd} data-testid="save-training-btn">Ajouter</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent data-testid="edit-training-dialog">
          <DialogHeader>
            <DialogTitle>Modifier la formation</DialogTitle>
            <DialogDescription>
              Correction directe des informations. L&apos;historique existant n&apos;est pas modifié.
              Pour enregistrer un repassage, utilisez le bouton <RefreshCw className="h-3 w-3 inline" /> Repasser.
            </DialogDescription>
          </DialogHeader>
          <TrainingFormFields form={form} setForm={setForm} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Annuler</Button>
            <Button onClick={handleEdit} data-testid="update-training-btn">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Renew dialog */}
      <Dialog open={renewOpen} onOpenChange={setRenewOpen}>
        <DialogContent data-testid="renew-training-dialog">
          <DialogHeader>
            <DialogTitle>Repasser la formation</DialogTitle>
            <DialogDescription>
              Les dates actuelles seront archivées dans l&apos;historique. Renseignez les nouvelles.
            </DialogDescription>
          </DialogHeader>
          {selected && (
            <div className="rounded-md bg-slate-50 border p-3 text-sm space-y-1">
              <div><span className="text-muted-foreground">Formation : </span><b>{selected.name}</b></div>
              <div className="text-muted-foreground">
                Actuellement valide du {formatDate(selected.training_date)} au{" "}
                {selected.expiry_date ? formatDate(selected.expiry_date) : "—"}
              </div>
            </div>
          )}
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Date du repassage *</Label>
                <Input
                  type="date"
                  value={renewForm.training_date}
                  onChange={(e) => setRenewForm({ ...renewForm, training_date: e.target.value })}
                  data-testid="renew-training-date"
                />
              </div>
              <div>
                <Label>Nouvelle fin de validité</Label>
                <Input
                  type="date"
                  value={renewForm.expiry_date}
                  onChange={(e) => setRenewForm({ ...renewForm, expiry_date: e.target.value })}
                  data-testid="renew-expiry-date"
                />
              </div>
            </div>
            <div>
              <Label>Notes (optionnel)</Label>
              <Textarea
                rows={2}
                value={renewForm.notes}
                onChange={(e) => setRenewForm({ ...renewForm, notes: e.target.value })}
                placeholder="Ex : recyclage effectué en présentiel, formateur X…"
                data-testid="renew-notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenewOpen(false)}>Annuler</Button>
            <Button onClick={handleRenew} data-testid="confirm-renew-btn">Enregistrer le repassage</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History dialog */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent data-testid="history-training-dialog">
          <DialogHeader>
            <DialogTitle>Historique des repassages</DialogTitle>
            {selected && (
              <DialogDescription>
                {selected.name} — validité en cours du <b>{formatDate(selected.training_date)}</b>
                {selected.expiry_date && <> au <b>{formatDate(selected.expiry_date)}</b></>}
              </DialogDescription>
            )}
          </DialogHeader>
          {selected && (
            <div className="space-y-2 max-h-[50vh] overflow-y-auto">
              <div className="text-xs uppercase tracking-wide text-muted-foreground px-1 mt-2">
                Périodes antérieures ({(selected.history || []).length})
              </div>
              {(selected.history || []).length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">Aucune période antérieure enregistrée.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date de formation</TableHead>
                      <TableHead>Fin de validité</TableHead>
                      <TableHead>Notes</TableHead>
                      <TableHead>Archivé le</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {[...(selected.history || [])]
                      .reverse()
                      .map((h, idx) => (
                        <TableRow key={idx} data-testid={`history-row-${idx}`}>
                          <TableCell>{h.training_date ? formatDate(h.training_date) : "—"}</TableCell>
                          <TableCell>{h.expiry_date ? formatDate(h.expiry_date) : "—"}</TableCell>
                          <TableCell className="text-sm text-muted-foreground max-w-xs truncate" title={h.notes || ""}>
                            {h.notes || "—"}
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">
                            {h.recorded_at ? formatDate(h.recorded_at) : "—"}
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setHistoryOpen(false)}>Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Shared field block for add/edit dialogs
const TrainingFormFields = ({ form, setForm }) => (
  <div className="space-y-3">
    <div>
      <Label>Nom de la formation *</Label>
      <Input
        value={form.name}
        onChange={(e) => setForm({ ...form, name: e.target.value })}
        placeholder="Habilitation électrique, CACES..."
        data-testid="train-name"
      />
    </div>
    <div>
      <Label>Organisme</Label>
      <Input
        value={form.organization}
        onChange={(e) => setForm({ ...form, organization: e.target.value })}
        placeholder="Nom de l'organisme de formation"
        data-testid="train-organization"
      />
    </div>
    <div className="grid grid-cols-2 gap-3">
      <div>
        <Label>Date de formation *</Label>
        <Input
          type="date"
          value={form.training_date}
          onChange={(e) => setForm({ ...form, training_date: e.target.value })}
          data-testid="train-date"
        />
      </div>
      <div>
        <Label>Date fin de validité</Label>
        <Input
          type="date"
          value={form.expiry_date}
          onChange={(e) => setForm({ ...form, expiry_date: e.target.value })}
          data-testid="train-expiry"
        />
      </div>
    </div>
    <div>
      <Label>Notes</Label>
      <Input
        value={form.notes}
        onChange={(e) => setForm({ ...form, notes: e.target.value })}
        data-testid="train-notes"
      />
    </div>
  </div>
);

export default TrainingsTab;
